import { useState, useEffect, useCallback, useRef } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { useTranslation } from 'react-i18next';

const SLIDE_DURATION = 5000;

export default function Testimonials() {
  const [current, setCurrent] = useState(0);
  const [phase, setPhase] = useState<'in' | 'out'>('in');
  const [barKey, setBarKey] = useState(0);
  const autoTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: panelRef, isVisible: panelVisible } = useScrollAnimation();
  const { t } = useTranslation();

  const testimonials = [
    { quote: t('test_1_quote'), name: t('test_1_name'), role: t('test_1_role'), tag: t('test_1_tag'), image: 'https://storage.readdy-site.link/project_files/09ac3b88-05d8-4a69-af11-22c685bd56bb/172daa6f-b774-4b3f-a133-d11d8f3aa528____4.jpg?v=bbac39d503c253a0ef3444fa9239ada7' },
    { quote: t('test_2_quote'), name: t('test_2_name'), role: t('test_2_role'), tag: t('test_2_tag'), image: 'https://storage.readdy-site.link/project_files/09ac3b88-05d8-4a69-af11-22c685bd56bb/a98c0793-a087-44db-b9f3-4405b362e1dc_POV2.jpg?v=46a6effdfe4d3dda2b69c6999da35d63' },
    { quote: t('test_3_quote'), name: t('test_3_name'), role: t('test_3_role'), tag: t('test_3_tag'), image: 'https://storage.readdy-site.link/project_files/09ac3b88-05d8-4a69-af11-22c685bd56bb/5f91a397-35d1-40d8-ba07-f581af972b8c_1.jpg?v=e80b17d5d6b43e18ea89e5ab8a8247fd' },
    { quote: t('test_4_quote'), name: t('test_4_name'), role: t('test_4_role'), tag: t('test_4_tag'), image: 'https://storage.readdy-site.link/project_files/09ac3b88-05d8-4a69-af11-22c685bd56bb/e69da44b-59e6-4f04-aa56-a58dce34bfa6_2026-03-02-19.00.09.png?v=68c8495e94cd9d3ffdb4fedf7218b3f2' },
  ];

  const switchTo = useCallback((i: number) => {
    if (i === current) return;
    if (autoTimer.current) clearTimeout(autoTimer.current);
    setPhase('out');
    setTimeout(() => { setCurrent(i); setBarKey((k) => k + 1); setPhase('in'); }, 320);
  }, [current]);

  useEffect(() => {
    autoTimer.current = setTimeout(() => {
      const next = (current + 1) % testimonials.length;
      setPhase('out');
      setTimeout(() => { setCurrent(next); setBarKey((k) => k + 1); setPhase('in'); }, 320);
    }, SLIDE_DURATION);
    return () => { if (autoTimer.current) clearTimeout(autoTimer.current); };
  }, [current, barKey, testimonials.length]);

  const active = testimonials[current];
  const stagger = (delay: number) => `transition-all duration-500 ${phase === 'in' ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'} [transition-delay:${delay}ms]`;

  return (
    <section className="w-full bg-[#FAF8F5] py-20 lg:py-36 overflow-x-clip">
      <style>{`@keyframes bar-fill { from { width: 0% } to { width: 100% } }`}</style>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div ref={headerRef} className={`mb-12 md:mb-16 transition-all duration-700 ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <p className="font-label text-xs tracking-[0.3em] text-stone-400 uppercase mb-4">{t('test_label')}</p>
              <h2 className="font-zh-serif text-3xl lg:text-5xl text-stone-900 font-light mb-4">{t('test_title')}</h2>
              <div className="w-12 h-px bg-stone-300" />
            </div>
            <p className="font-noto text-stone-400 text-sm font-light leading-6 hidden md:block">{t('test_subtitle')}</p>
          </div>
        </div>
        <div ref={panelRef} className={`flex flex-col lg:flex-row gap-8 lg:gap-14 transition-all duration-700 delay-100 ${panelVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="flex-1 flex flex-col justify-between min-h-[380px] lg:min-h-[500px]">
            <div className="flex-1 flex flex-col justify-center">
              <div className={stagger(0) + ' flex items-center gap-3 mb-6'}>
                <span className="font-label text-[10px] tracking-[0.3em] text-stone-400 uppercase px-3 py-1.5 border border-stone-200 rounded-full whitespace-nowrap">{active.tag}</span>
                <span className="font-display text-stone-200 text-3xl font-light select-none tabular-nums">{String(current + 1).padStart(2, '0')}</span>
              </div>
              <div className={stagger(100)}>
                <p className="font-noto text-stone-800 text-xl md:text-2xl lg:text-[1.6rem] font-light leading-[1.85] mb-8">&ldquo;{active.quote}&rdquo;</p>
              </div>
            </div>
            <div className="mt-10 lg:mt-12">
              <div className="flex gap-2.5">
                {testimonials.map((t2, i) => (
                  <button key={i} onClick={() => switchTo(i)} className="flex-1 group cursor-pointer" aria-label={t2.name}>
                    <div className="relative h-[2px] bg-stone-200 rounded-full overflow-hidden mb-2">
                      {i < current && <div className="absolute inset-y-0 left-0 w-full bg-stone-700 rounded-full" />}
                      {i === current && <div key={barKey} className="absolute inset-y-0 left-0 h-full bg-stone-800 rounded-full" style={{ animation: `bar-fill ${SLIDE_DURATION}ms linear forwards` }} />}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className={`font-label text-[10px] tracking-wider transition-colors duration-300 ${i === current ? 'text-stone-800' : 'text-stone-300 group-hover:text-stone-500'}`}>{t2.tag}</span>
                      <span className={`font-display text-xs tabular-nums transition-colors duration-300 ${i === current ? 'text-stone-400' : 'text-stone-200'}`}>{String(i + 1).padStart(2, '0')}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="relative w-full h-[300px] md:h-[400px] lg:w-[44%] lg:h-auto lg:min-h-[500px] rounded-2xl overflow-hidden shrink-0">
            {testimonials.map((t2, i) => (
              <img key={t2.name} src={t2.image} alt={t2.name} className={`absolute inset-0 w-full h-full object-cover object-center transition-all duration-700 ${i === current ? 'opacity-100 scale-100' : 'opacity-0 scale-105'}`} loading="lazy" decoding="async" />
            ))}
            <div className="absolute inset-0 bg-gradient-to-br from-black/5 via-transparent to-black/20 pointer-events-none" />
          </div>
        </div>
      </div>
    </section>
  );
}
